// Audit.jsx — pagina Audit Log con filtri, tabella, dettaglio, export
// Route: /audit
// Richiede backend route: GET /api/audit e GET /api/audit/:instanceId

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTimeLong, formatDateTime } from '../i18n/dateUtils';
import { ModalOverlay, useSort, SortableHeader } from '../components/UI';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const LIMIT = 50;

const ACTION_COLORS = {
  upload: '#06b6d4', query: '#f59e0b', create: '#10b981',
  delete: '#ef4444', error: '#ef4444', auth: '#8b5cf6',
  index: '#06b6d4', system: '#475569',
};

function Badge({ action }) {
  const { t } = useTranslation('audit');
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 600, fontFamily: 'var(--font-mono)',
      background: `${ACTION_COLORS[action] || '#475569'}20`,
      color: ACTION_COLORS[action] || '#475569',
      border: `1px solid ${ACTION_COLORS[action] || '#475569'}40`,
    }}>
      {t(`actions.${action}`, { defaultValue: action })}
    </span>
  );
}

function DetailModal({ event, onClose }) {
  const { t } = useTranslation('audit');
  if (!event) return null;

  const ts = event.timestamp ? formatDateTimeLong(event.timestamp) : '—';

  function copyRaw() {
    navigator.clipboard.writeText(JSON.stringify(event, null, 2))
      .catch(() => {});
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 28,
        width: 'min(600px, 100%)',
        maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        animation: 'fadeIn 0.2s ease',
      }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
          {t('detail')}
        </div>

        {/* Fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '10px 16px', marginBottom: 20 }}>
          {[
            [t('timestamp'), ts],
            [t('type'), <Badge key="b" action={event.action} />],
            [t('instance'), event.instanceName || '—'],
            [t('instanceId'), event.instanceId || '—'],
            [t('detailLabel'), event.detail || '—'],
          ].map(([label, value]) => (
            <>
              <span key={`l-${label}`} style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.1em', paddingTop: 2 }}>
                {label}
              </span>
              <span key={`v-${label}`} style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all', lineHeight: 1.6 }}>
                {value}
              </span>
            </>
          ))}
        </div>

        {/* Raw JSON */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.1em', marginBottom: 8 }}>
            {t('raw')}
          </div>
          <pre style={{
            background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 8,
            padding: 14, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)',
            overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            lineHeight: 1.6, margin: 0,
          }}>
            {JSON.stringify(event, null, 2)}
          </pre>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnStyle}>{t('close', { ns: 'common' })}</button>
          <button onClick={copyRaw} style={{ ...btnStyle, background: 'var(--accent)', color: '#fff' }}>
            📋 {t('copy', { ns: 'common' })}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

const btnStyle = {
  padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-3)', color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
  fontSize: 12, cursor: 'pointer',
};

export default function Audit() {
  const { t } = useTranslation('audit');
  const [instances, setInstances] = useState([]);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filterInstance, setFilterInstance] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [cachedEvents, setCachedEvents] = useState([]);

  // Load instances for filter dropdown
  useEffect(() => {
    fetch(`${API_BASE}/instances`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setInstances(d?.instances || []))
      .catch(() => {});
  }, []);

  const loadAudit = useCallback(async (resetOffset = true) => {
    const newOffset = resetOffset ? 0 : offset;
    if (resetOffset) setOffset(0);

    setLoading(true);
    const params = new URLSearchParams({ limit: LIMIT, offset: newOffset });
    if (filterAction) params.set('action', filterAction);
    if (filterFrom)   params.set('from', filterFrom);
    if (filterTo)     params.set('to', filterTo + 'T23:59:59');

    const endpoint = filterInstance
      ? `${API_BASE}/audit/${filterInstance}?${params}`
      : `${API_BASE}/audit?${params}`;

    try {
      const res = await fetch(endpoint, { credentials: 'include' });
      const data = await res.json();
      setEvents(data.events || []);
      setCachedEvents(data.events || []);
      setTotal(data.total || 0);
    } catch {
      setEvents([]);
      setTotal(0);
    }
    setLoading(false);
  }, [filterInstance, filterAction, filterFrom, filterTo, offset]);

  useEffect(() => { loadAudit(true); }, [filterInstance, filterAction, filterFrom, filterTo]);

  function exportData(format) {
    if (!cachedEvents.length) return;
    let content, filename, mime;

    if (format === 'csv') {
      const headers = ['timestamp', 'action', 'instanceName', 'instanceId', 'detail'];
      const rows = cachedEvents.map(ev =>
        headers.map(h => `"${String(ev[h] || '').replace(/"/g, '""')}"`).join(',')
      );
      content = [headers.join(','), ...rows].join('\n');
      filename = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      mime = 'text/csv;charset=utf-8;';
    } else {
      content = JSON.stringify({ exported: new Date().toISOString(), total: cachedEvents.length, events: cachedEvents }, null, 2);
      filename = `audit-log-${new Date().toISOString().slice(0, 10)}.json`;
      mime = 'application/json';
    }

    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;
  const { sorted: sortedEvents, sortKey: evSortKey, sortDir: evSortDir, handleSort: handleEvSort } = useSort(events, 'timestamp', 'desc');

  return (
    <div className="page-content animate-fade">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700 }}>
          {t('title')}
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => exportData('csv')} style={btnStyle}>⬇ CSV</button>
          <button onClick={() => exportData('json')} style={btnStyle}>⬇ JSON</button>
          <button onClick={() => loadAudit(true)} style={{ ...btnStyle, color: 'var(--accent)' }}>↺ {t('refresh', { ns: 'common' })}</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={filterInstance} onChange={e => setFilterInstance(e.target.value)}
          style={selectStyle}
        >
          <option value="">{t('allInstances')}</option>
          {instances.map(i => (
            <option key={i.id} value={i.id}>{i.icon || '⬡'} {i.name}</option>
          ))}
        </select>

        <select data-testid="filter-action" value={filterAction} onChange={e => setFilterAction(e.target.value)} style={selectStyle}>
          <option value="">{t('allEvents')}</option>
          <option value="upload">📄 {t('actions.upload')}</option>
          <option value="query">💬 {t('actions.query')}</option>
          <option value="create">＋ {t('actions.create')}</option>
          <option value="delete">✕ {t('actions.delete')}</option>
          <option value="auth">🔐 {t('actions.auth')}</option>
          <option value="index">🔢 {t('actions.index')}</option>
          <option value="error">⚠ {t('actions.error')}</option>
        </select>

        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={inputStyle} />
        <input type="date" value={filterTo}   onChange={e => setFilterTo(e.target.value)}   style={inputStyle} />

        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
          {t('events', { count: total })}
        </span>
      </div>

      {/* Table */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--bg-2)' }}>
              {[
                { label: t('timestamp'), colKey: 'timestamp' },
                { label: t('type'),      colKey: 'action' },
                { label: t('instance'),  colKey: 'instanceName' },
                { label: t('detailLabel'), colKey: 'detail' },
              ].map(({ label, colKey }) => (
                <SortableHeader key={colKey} label={label} colKey={colKey}
                  sortKey={evSortKey} sortDir={evSortDir} onSort={handleEvSort}
                  style={{ borderBottom: '1px solid var(--border)' }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>
                {t('loading', { ns: 'common' })}
              </td></tr>
            ) : sortedEvents.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                {t('noEvents')}
              </td></tr>
            ) : sortedEvents.map((ev, i) => {
              const ts = ev.timestamp ? formatDateTime(ev.timestamp) : '—';
              return (
                <tr
                  key={i}
                  onClick={() => setSelectedEvent(ev)}
                  style={{
                    borderBottom: '1px solid var(--border)', cursor: 'pointer',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '9px 14px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{ts}</td>
                  <td style={{ padding: '9px 14px' }}><Badge action={ev.action} /></td>
                  <td style={{ padding: '9px 14px', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{ev.instanceName || '—'}</td>
                  <td style={{
                    padding: '9px 14px', maxWidth: 400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: 'var(--text-2)',
                  }} title={ev.detail}>{ev.detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-3)' }}>
          <button
            disabled={offset === 0} onClick={() => { setOffset(Math.max(0, offset - LIMIT)); loadAudit(false); }}
            style={{ ...btnStyle, opacity: offset === 0 ? 0.4 : 1 }}
          >←</button>
          <span>{t('pageOfTotal', { ns: 'common', page: currentPage, total: totalPages })}</span>
          <button
            disabled={offset + LIMIT >= total} onClick={() => { setOffset(offset + LIMIT); loadAudit(false); }}
            style={{ ...btnStyle, opacity: offset + LIMIT >= total ? 0.4 : 1 }}
          >→</button>
        </div>
      )}

      {/* Detail modal */}
      {selectedEvent && <DetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
    </div>
  );
}

const selectStyle = {
  background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 8,
  padding: '6px 10px', color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
  fontSize: 12, outline: 'none', cursor: 'pointer', minWidth: 160,
};

const inputStyle = {
  background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 8,
  padding: '6px 10px', color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
  fontSize: 12, outline: 'none',
};
